/**
 * 태그 에디터 메인 컴포넌트
 * @description 플레이스홀더를 카드 형태로 표시하는 리치 텍스트 에디터.
 *   #{변수} 형태를 태그 UI로 보여주고, / 입력 또는 ref를 통한 삽입을 지원한다.
 */

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { cn } from "@/lib/utils";
import { PlaceholderTagNode } from "./PlaceholderTagNode";
import {
  parseTemplateToContent,
  serializeContentToTemplate,
} from "./tagEditorUtils";
import { SlashCommandPopup } from "../SlashCommandPopup";
import type { PlaceholderConfig, TagEditorRef } from "../../types/sms.types";

interface TagEditorProps {
  /** 템플릿 값 (#{...} 형식) */
  value: string;
  /** 값 변경 핸들러 */
  onChange: (value: string) => void;
  /** 사용 가능한 플레이스홀더 목록 */
  placeholders: PlaceholderConfig[];
  /** 읽기 전용 여부 */
  readOnly?: boolean;
  /** 추가 CSS 클래스 */
  className?: string;
  /** 최소 높이 (fitParent가 false일 때 사용) */
  minHeight?: string;
  /** 부모 높이에 맞추고 내부 스크롤 사용 */
  fitParent?: boolean;
  /** 빈 에디터 플레이스홀더 텍스트 */
  placeholderText?: string;
}

export const TagEditor = forwardRef<TagEditorRef, TagEditorProps>(
  (
    {
      value,
      onChange,
      placeholders,
      readOnly = false,
      className,
      minHeight = "200px",
      fitParent = false,
      placeholderText = "메시지를 입력하세요...",
    },
    ref,
  ) => {
    const [slashOpen, setSlashOpen] = useState(false);
    const [slashPosition, setSlashPosition] = useState<{
      top: number;
      left: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: false,
          orderedList: false,
          codeBlock: false,
          blockquote: false,
          code: false,
          strike: false,
          bold: false,
          italic: false,
        }),
        Placeholder.configure({
          placeholder: placeholderText,
          emptyEditorClass: "is-editor-empty",
        }),
        PlaceholderTagNode,
      ],
      content: parseTemplateToContent(value, placeholders),
      editable: !readOnly,
      editorProps: {
        attributes: {
          class: cn("prose prose-sm max-w-none", "focus:outline-none", "min-h-[inherit]"),
        },
      },
      onUpdate: ({ editor }) => {
        const template = serializeContentToTemplate(editor.getJSON());
        onChange(template);
      },
    });

    // 외부 value 변경 시 에디터 내용 동기화
    useEffect(() => {
      if (editor && !editor.isFocused) {
        const currentTemplate = serializeContentToTemplate(editor.getJSON());
        if (currentTemplate !== value) {
          editor.commands.setContent(
            parseTemplateToContent(value, placeholders),
          );
        }
      }
    }, [value, editor, placeholders]);

    const insertPlaceholder = useCallback(
      (config: PlaceholderConfig) => {
        if (!editor) return;
        editor
          .chain()
          .focus()
          .insertPlaceholder(config.placeholder, config.label, config.color)
          .run();
      },
      [editor],
    );

    useImperativeHandle(
      ref,
      () => ({
        insertPlaceholder,
        focus: () => editor?.chain().focus().run(),
        getTemplate: () =>
          editor ? serializeContentToTemplate(editor.getJSON()) : "",
      }),
      [editor, insertPlaceholder],
    );

    const handleContainerClick = useCallback(() => {
      if (slashOpen) {
        setSlashOpen(false);
      }
      if (!readOnly && editor) {
        editor.chain().focus().run();
      }
    }, [editor, readOnly, slashOpen]);

    const handleSlashSelect = useCallback(
      (config: PlaceholderConfig) => {
        insertPlaceholder(config);
        setSlashOpen(false);
      },
      [insertPlaceholder],
    );

    // / 입력 감지 → 슬래시 팝업 열기
    useEffect(() => {
      if (!editor || readOnly) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "/" && !slashOpen) {
          event.preventDefault();

          const selection = window.getSelection();
          let position = { top: 40, left: 12 };

          if (selection && selection.rangeCount > 0 && containerRef.current) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            if (rect.width > 0 || rect.height > 0 || rect.top > 0) {
              position = {
                top: rect.bottom - containerRect.top + 4,
                left: Math.max(0, rect.left - containerRect.left),
              };
            }
          }

          setSlashPosition(position);
          setSlashOpen(true);
        }
      };

      const editorElement = editor.view.dom;
      editorElement.addEventListener("keydown", handleKeyDown);

      return () => {
        editorElement.removeEventListener("keydown", handleKeyDown);
      };
    }, [editor, readOnly, slashOpen]);

    // 외부 클릭 시 슬래시 팝업 닫기
    useEffect(() => {
      if (!slashOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setSlashOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [slashOpen]);

    return (
      <div
        ref={containerRef}
        className={cn(
          "relative border rounded-md bg-white transition-colors cursor-text",
          "focus-within:border-ring",
          readOnly && "bg-gray-50 cursor-not-allowed",
          fitParent && "h-full flex flex-col",
          className,
        )}
        style={fitParent ? undefined : { minHeight }}
        onClick={handleContainerClick}
      >
        <EditorContent
          editor={editor}
          className={cn(
            "p-3",
            fitParent ? "flex-1 overflow-y-auto" : "h-full",
            "[&_.ProseMirror]:min-h-[inherit]",
            "[&_.ProseMirror]:outline-none",
            "[&_.ProseMirror.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
            "[&_.ProseMirror.is-editor-empty:first-child::before]:text-gray-400",
            "[&_.ProseMirror.is-editor-empty:first-child::before]:float-left",
            "[&_.ProseMirror.is-editor-empty:first-child::before]:h-0",
            "[&_.ProseMirror.is-editor-empty:first-child::before]:pointer-events-none",
          )}
        />

        <SlashCommandPopup
          open={slashOpen}
          onClose={() => setSlashOpen(false)}
          placeholders={placeholders}
          onSelect={handleSlashSelect}
          position={slashPosition ?? undefined}
        />
      </div>
    );
  },
);

TagEditor.displayName = "TagEditor";

export default TagEditor;
